/**
 * Procedure catalog — SNOMED-only mode.
 * The built-in local procedure catalog (./data/*) was removed: procedure terms
 * come from the SNOMED CT catalog (server-seeded) and the user's own history.
 * This module keeps the light helpers the shell needs: name normalization,
 * specialties for profile/scoped search, display-name formatting and the
 * history-based length-of-stay suggestion.
 */
import type { Surgery } from '../types';
import { median, sortByDateDesc } from '../stats';
import type { CatalogSpecialty } from './types';
import { SPECIALTIES } from './specialties';

export type {
  CatalogProcedure,
  CatalogSpecialty,
  CustomProcedure,
  TechniqueVariant,
  TechniqueVisibility,
} from './types';

/** Case/accent-insensitive normalization for name matching. */
export function normalizeName(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim();
}

/** Display name for a stored category: shown as entered, first letter capitalized. */
export function getProcedureDisplayName(name: string): string {
  const clean = name.trim();
  if (!clean) return clean;
  return clean.charAt(0).toUpperCase() + clean.slice(1);
}

/** Canonical name for a stored category: identity (no local catalog to normalize against). */
export function getCanonicalProcedureName(name: string): string {
  return name.trim();
}

export function listSpecialties(): CatalogSpecialty[] {
  return SPECIALTIES;
}

/** Matches a free-text `Profile.especialidad` against the catalog specialties. */
export function getSpecialtyByName(name: string): CatalogSpecialty | undefined {
  const q = normalizeName(name);
  if (!q) return undefined;
  // Match against both the English and Spanish display names, since the stored
  // value depends on the UI language when the specialty was chosen.
  return SPECIALTIES.find((s) =>
    [s.name, s.nameEs ?? s.name].some((label) => {
      const n = normalizeName(label);
      return n === q || n.includes(q) || q.includes(n);
    }),
  );
}

export function getSpecialtyById(id: string): CatalogSpecialty | undefined {
  return SPECIALTIES.find((s) => s.id === id);
}

/** Localized display name for a specialty. */
export function specialtyLabel(s: CatalogSpecialty, lang: string): string {
  return lang.startsWith('es') ? (s.nameEs ?? s.name) : s.name;
}

/**
 * Resolves a stored (free-text) specialty to its display name in the given
 * language. Uses an EXACT normalized match against the catalog's English and
 * Spanish names, so a value saved in one language shows correctly in the other
 * — while any custom/free-text value is returned untouched (no fuzzy matching,
 * unlike getSpecialtyByName, so partial names are never rewritten).
 */
export function localizedSpecialtyName(raw: string, lang: string): string {
  const q = normalizeName(raw);
  if (!q) return raw;
  const match = SPECIALTIES.find(
    (s) => normalizeName(s.name) === q || normalizeName(s.nameEs ?? s.name) === q,
  );
  return match ? specialtyLabel(match, lang) : raw;
}

export interface HospitalizacionSuggestion {
  suggestion?: number;
  medianWithout?: number;
  medianWith?: number;
}

/**
 * Length-of-stay suggestion from the user's own history only (no catalog
 * default in SNOMED-only mode). If the user has previously recorded a
 * complicated case of the same procedure, the suggestion is raised to at least
 * that value. Returns separate medians for previous cases with/without
 * long-term complications.
 */
export function suggestHospitalizacionDias(
  _procedure: unknown,
  sameProcedureSurgeries: Surgery[],
): HospitalizacionSuggestion {
  const previous = sameProcedureSurgeries.filter((s) => s.seguimiento?.diasHospitalizacion != null);
  // Optional-chain through complicacionesLargoPlazo — list summaries omit the array.
  const withComp = previous.filter((s) => (s.seguimiento?.complicacionesLargoPlazo?.length ?? 0) > 0);
  const withoutComp = previous.filter((s) => (s.seguimiento?.complicacionesLargoPlazo?.length ?? 0) === 0);

  const medianWith = median(withComp.map((s) => s.seguimiento!.diasHospitalizacion!));
  const medianWithout = median(withoutComp.map((s) => s.seguimiento!.diasHospitalizacion!));

  let suggestion: number | undefined;
  if (withComp.length > 0) {
    const lastComplicated = sortByDateDesc(withComp)[0];
    suggestion = lastComplicated.seguimiento!.diasHospitalizacion!;
  }

  return {
    suggestion,
    medianWith,
    medianWithout,
  };
}
